import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { X } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import MitgliedSuche from '@/components/MitgliedSuche';
import { useAuth } from '@/contexts/AuthContext';
import { trainingslogAPI, trainingsplanerAPI } from '@/lib/api';

/* Trainingsplaner › Fitness-Zyklus: „Für wen ist dieser Plan?“ (Betreiber 10.10.2026: ein PT-Kunde
   bekommt einen eigenen Trainingsplan). Der Zyklus wird wie bisher in der Trainer-App geplant; hier
   weist die Leitung ihm Personen zu. Die Person sieht ihn in der App im Reiter „Training“, sobald
   am Mitglied Check-Up und PT-Buchung bestätigt sind und der Plan freigegeben ist und läuft.
   Wer was sieht, entscheidet der Server (backend/trainingslog/zugang.py) — hier steht je Person,
   was noch fehlt. */

const FEHLT = {
  vertrag: 'keine laufende Mitgliedschaft',
  checkup: 'Check-Up fehlt',
  pt_buchung: 'PT-Buchung nicht bestätigt',
  standard: 'sieht nur den Standardplan (Auswahl „fest“)',
};

export default function PlanPersonen({ z, onGeaendert }) {
  const { isAdminPermissions, userPermissions } = useAuth();
  // Zuweisen darf, wer den Planer bearbeiten darf — der Server prüft dasselbe (Ersteller, Headcoach, Leitung)
  // Archivierte Zyklen sind schreibgeschützt (nur der Name lässt sich noch ändern, components/PlanName.js)
  const archiviert = z.status === 'archiviert';
  const darf = !archiviert && (isAdminPermissions || !!(userPermissions || {})['trainingsplaner.edit']);
  const [daten, setDaten] = useState(null);
  const [fehler, setFehler] = useState(null);
  const [busy, setBusy] = useState(false);
  const lauf = useRef(0);

  const lade = useCallback(async () => {
    const nr = ++lauf.current;
    try {
      const r = await trainingslogAPI.zyklus(z.id);
      if (nr === lauf.current) { setDaten(r.data); setFehler(null); }
    } catch (e) {
      if (nr === lauf.current) setFehler(e?.response?.status === 403 ? 'Für diese Liste fehlt dir das Recht „Trainingshistorie & Gesundheitsdaten einsehen“.' : 'Die Personen konnten nicht geladen werden.');
    }
  }, [z.id]);
  useEffect(() => { setDaten(null); lade(); }, [lade]);
  // Der Zyklus wurde daneben geändert (umbenannt): Version still nachziehen, sonst scheitert das
  // nächste Zuweisen mit 409. Nur wenn die Übersicht NEUER ist — nach dem eigenen Speichern ist sie kurz älter.
  const version = daten?.version;
  useEffect(() => { if (version != null && z.version > version) lade(); }, [z.version, version, lade]);

  const setzen = async (ids, erfolg) => {
    if (!daten) return;
    // Die Suche leert ihr Feld sofort — eine Auswahl während des Speicherns darf nicht still verschwinden
    if (busy) { toast.info('Die letzte Änderung wird noch gespeichert – bitte gleich noch einmal wählen.'); return; }
    setBusy(true);
    try {
      await trainingsplanerAPI.athleten(z.id, daten.version, ids);
      toast.success(erfolg);
      onGeaendert?.();
    } catch (e) {
      toast.error(e?.response?.status === 409 ? 'Der Zyklus wurde inzwischen geändert – die Liste ist neu geladen, bitte noch einmal.' : (e?.response?.data?.detail || 'Nicht gespeichert'));
    }
    await lade();
    setBusy(false);
  };

  const kopf = <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Für wen ist dieser Plan?</p>;
  if (fehler) return <div className="space-y-1" data-testid={`plan-personen-${z.id}`}>{kopf}<p className="text-xs text-muted-foreground">{fehler}</p></div>;
  if (!daten) return <div className="space-y-1" aria-busy="true" aria-label="Personen werden geladen">{kopf}<Skeleton className="h-8" /></div>;

  const personen = Array.isArray(daten.personen) ? daten.personen : [];
  const ids = personen.map((p) => p.member_id);
  const hatKurse = (z.kurse || []).length > 0;
  // Wer in einem Kurs des Zyklus dabei ist, sieht ihn schon darüber — auch ohne PT-Zugang
  const ueberKurs = new Set((daten.freigeschaltet || []).filter((f) => f.grund === 'kurs').map((f) => f.member_id));
  // Archiviert: Der Plan erscheint nie mehr in der App — was einer Person dafür fehlt, ist dann ohne Belang
  const fehltPt = (p) => !archiviert && !!p.fehlt && !ueberKurs.has(p.member_id);
  const d = new Date();
  const heute = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const planLage = archiviert ? 'der Plan ist archiviert' : z.status !== 'freigegeben' ? 'der Plan ist noch nicht freigegeben'
    : z.enddatum && z.enddatum < heute ? 'der Plan ist beendet' : 'der Plan läuft heute noch nicht';
  const bereit = (p) => (p.team ? 'Trainer oder Leitung' : daten.laeuft ? 'sieht den Plan in der App' : `bereit – ${planLage}`);
  const lage = (p) => (archiviert ? '' : ueberKurs.has(p.member_id) && p.fehlt ? 'sieht den Plan über den Kurs' : p.fehlt ? FEHLT[p.fehlt] || 'PT-Zugang fehlt' : bereit(p));

  return (
    <div className="space-y-1.5" data-testid={`plan-personen-${z.id}`}>
      {kopf}
      {personen.length === 0 ? (
        <p className="text-sm">
          Noch niemandem zugewiesen.
          <span className="text-muted-foreground">{z.kurse?.length ? ' Den Plan sehen die Teilnehmer seiner Kurse.' : ' Ein Plan ohne Kurs und ohne Personen ist nur für Trainer und Leitung sichtbar.'}</span>
        </p>
      ) : (
        <ul className="text-sm divide-y divide-border/40">
          {personen.map((p) => (
            <li key={p.member_id} className="py-1 flex flex-wrap items-baseline gap-x-2">
              <Link to={`/leads/${p.member_id}`} className="font-medium hover:underline break-words">{p.member_name || 'Ohne Namen'}</Link>
              <span className={`text-xs break-words ${fehltPt(p) ? 'text-amber-700 dark:text-amber-400' : 'text-muted-foreground'}`}>{lage(p)}</span>
              {darf && (
                <button type="button" disabled={busy} onClick={() => setzen(ids.filter((i) => i !== p.member_id), `${p.member_name || 'Person'} aus dem Plan genommen`)}
                  className="ml-auto inline-flex items-center gap-0.5 text-xs text-muted-foreground hover:text-red-600 disabled:opacity-50"
                  aria-label={`${p.member_name || 'Person'} aus dem Plan nehmen`} data-testid={`plan-person-weg-${p.member_id}`}>
                  <X className="w-3 h-3" /> entfernen
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      {personen.length > 0 && hatKurse && (
        <p className="text-xs text-amber-700 dark:text-amber-400">Achtung: Dieser Plan hängt auch an {z.kurse.map((k) => k.name).join(', ')} – zusätzlich sehen ihn alle Teilnehmer dieser Kurse. Ein eigener Plan braucht einen Zyklus ohne Kurs.</p>
      )}
      {personen.some((p) => fehltPt(p) && p.fehlt !== 'vertrag') && (
        <p className="text-xs text-muted-foreground">Check-Up und PT-Buchung bestätigst du am Mitglied im Reiter „Kraftlog“. Erst danach erscheint der Plan in der App.</p>
      )}
      {personen.some((p) => fehltPt(p) && p.fehlt === 'vertrag') && (
        <p className="text-xs text-muted-foreground">Ohne laufende Mitgliedschaft erscheint der Plan nicht in der App.</p>
      )}
      {darf ? (
        <div className="max-w-sm pt-1">
          <MitgliedSuche wert={null} placeholder="Person zuweisen – Name suchen…" tid={`plan-person-suche-${z.id}`}
            onWahl={(w) => { if (w && !ids.includes(w.id)) setzen([...ids, w.id], `${w.name} zugewiesen`); }} />
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">{archiviert ? 'Der Plan ist archiviert – Personen lassen sich nicht mehr ändern.' : 'Zuweisen kann, wer den Trainingsplaner bearbeiten darf.'}</p>
      )}
    </div>
  );
}
